/**
 * Stamp the exporting agency's credit onto a footer — at RENDER time, never in
 * the data.
 *
 * A template's footer carries the credit of the studio that built the site it
 * was crawled from. That credit is the truth about the TEMPLATE, and
 * data/template-developers.json plus the selector's "Built by" filter depend on
 * it staying that way. What ships from an export is a different artifact — the
 * user's own build — so the credit is swapped on the way out and the section
 * data is left alone. Deleting this module restores the original credits
 * everywhere, which is the property that makes the swap safe.
 *
 * This is deliberately NOT the operation that was once run over
 * `template-websites/`: that one edited the source, destroyed the original
 * attribution for 106 sites, and cost a full recovery pass. Same output,
 * opposite blast radius.
 *
 * Three cases, measured over the 358 parsed footers:
 *   292  a credit is already there  → rewrite its text and href in place
 *    58  a copyright line, no credit → append the credit into that line
 *     8  neither                     → append a credit line to the footer
 */

import { CREDIT_CLASS, CREDIT_KEYWORD } from './footer-credit.js';

/** The credit every export carries unless a project overrides it. */
export const DEFAULT_AGENCY_CREDIT = Object.freeze({
  text: 'Designed & Hand Coded By UI Compass',
  url: 'https://uicompass.com'
});

/**
 * Class the CodeStitch-derived footers put on the studio link. Exported so the
 * builder-context fidelity harness can recognise the line this module APPENDS
 * to a footer that had none — an intended builder addition, not a source
 * element the builder invented.
 */
export const CREDIT_LINK_CLASS = 'cs-copyright-link';

/** Containers that hold a copyright/credit line, best first. */
const CREDIT_CONTAINERS = [
  '.cs-copyright',
  '.cs-credit',
  '.credit',
  '[class*="copyright" i]',
  '[class*="credit" i]'
];

/** Where a synthesized credit line goes when there is no copyright line at all. */
const FOOTER_TAILS = ['.cs-bottom', '.cs-bottom-links', 'footer', ':scope'];

/**
 * The credit for a project: its own override when it has a valid one, else the
 * default. An override with a non-http(s) URL is refused rather than honoured —
 * the value ends up in an href.
 */
export function resolveAgencyCredit(state) {
  const override = state?.agencyCredit;
  if (!override || typeof override !== 'object') return DEFAULT_AGENCY_CREDIT;
  const text = typeof override.text === 'string' ? override.text.trim() : '';
  const url = typeof override.url === 'string' ? override.url.trim() : '';
  if (!text) return DEFAULT_AGENCY_CREDIT;
  if (url && !/^https?:\/\/[^\s]+$/i.test(url)) return DEFAULT_AGENCY_CREDIT;
  return Object.freeze({ text, url });
}

/** True when this element is, or sits inside, a credit/copyright line. */
function inCreditScope(el, root) {
  for (let node = el; node && node !== root.parentNode; node = node.parentElement) {
    if (node.nodeType === 1 && CREDIT_CLASS.test(node.getAttribute('class') || '')) return true;
  }
  return false;
}

/** The anchor currently carrying the studio credit, or null. */
function findCreditAnchor(root) {
  const anchors = [...root.querySelectorAll('a')];
  // An anchor whose own text names a builder — `Designed & Hand Coded By X`.
  for (const anchor of anchors) {
    const text = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
    const by = /\bby\b/i.exec(text);
    if (by && CREDIT_KEYWORD.test(text.slice(0, by.index))) return anchor;
  }
  // An anchor preceded by the phrase — `Designed By <a>X</a>`.
  for (const anchor of anchors) {
    if (!inCreditScope(anchor, root)) continue;
    if (endsWithCreditPhrase(textBefore(anchor))) return anchor;
  }
  // A credit-classed anchor pointing off-site, with no phrase anywhere.
  for (const anchor of anchors) {
    if (!CREDIT_CLASS.test(anchor.getAttribute('class') || '')) continue;
    const href = anchor.getAttribute('href') || '';
    if (/^https?:\/\//i.test(href)) return anchor;
  }
  return null;
}

/** The concatenated text of the nodes immediately before `el` in its parent. */
function textBefore(el) {
  let text = '';
  for (let node = el.previousSibling; node; node = node.previousSibling) {
    text = (node.textContent || '') + text;
    if (text.trim()) break;
  }
  return text.replace(/\s+/g, ' ');
}

function endsWithCreditPhrase(text) {
  const trimmed = String(text || '').trimEnd();
  if (!/\bby\b\s*:?$/i.test(trimmed)) return false;
  const by = /\bby\b\s*:?$/i.exec(trimmed);
  return CREDIT_KEYWORD.test(trimmed.slice(Math.max(0, by.index - 48), by.index));
}

/** The phrase that introduces a credit, anchored to the end of a string. */
const PHRASE_TAIL =
  /(?:designed?|developed?|hand[\s-]?coded|coded|built|made|crafted|created|website|web\s*site|web\s*design)[^<>]{0,45}$/i;

/** Connector left dangling once the phrase is cut — `© 2026 - ` keeps its dash. */
const TRAILING_CONNECTOR = /[\s\-–—·|,:]+$/;

/**
 * The last non-blank text node before `el` in document order.
 *
 * Direct-sibling walking is not enough: two thirds of the footers that carry a
 * phrase put it in its own element — `<span>Designed and Hand Coded by</span>
 * <a>Studio</a>` — so the text to edit is a cousin, not a sibling.
 */
function previousTextNode(el, root) {
  let node = el;
  while (node && node !== root) {
    if (node.previousSibling) {
      node = node.previousSibling;
      while (node.lastChild) node = node.lastChild;
      if (node.nodeType === 3 && (node.nodeValue || '').trim()) return node;
      continue;
    }
    node = node.parentNode;
  }
  return null;
}

/**
 * Drop the credit phrase sitting in front of the anchor.
 *
 * Without this the export reproduces the exact defect the bulk rewrite left on
 * 21 crawled sites: `Built By Designed & Hand Coded By UI Compass`. Our phrase
 * now lives inside the anchor, so the loose copy in front of it has to go —
 * along with the bare `by` in the cases where the phrase is only that.
 */
function stripLeadingPhrase(anchor, root) {
  const node = previousTextNode(anchor, root);
  if (!node) return;
  const value = node.nodeValue || '';
  const trimmed = value.trimEnd();
  const by = /\bby\b\s*:?$/i.exec(trimmed);
  if (!by) return;
  const head = trimmed.slice(0, by.index);
  // Cut the whole authorship clause when there is one; otherwise cut just the
  // dangling `by`, because our own phrase supplies its own.
  const tail = head.slice(-48);
  const phrase = PHRASE_TAIL.exec(tail);
  const keep = phrase ? head.slice(0, head.length - tail.length + phrase.index) : head;
  node.nodeValue = keep.trimEnd().replace(TRAILING_CONNECTOR, '') + ' ';

  // If the phrase had its own element and that element is now empty, drop it
  // so the footer does not ship a stray <span> with a separator inside.
  const host = node.parentElement;
  if (
    host &&
    host !== root &&
    !(host.textContent || '').trim() &&
    !host.querySelector('img, svg, picture, br')
  ) {
    host.remove();
  }
}

function applyCreditToAnchor(anchor, credit, doc) {
  // Keep the anchor's own padding. Several footers write the separator INSIDE
  // the link — `<a>Bluff City Web Designs </a>© Copyright 2024` — so replacing
  // the text wholesale runs the credit into whatever follows it.
  const original = anchor.textContent || '';
  const lead = /^\s*/.exec(original)[0] ? ' ' : '';
  const trail = /\s$/.test(original) ? ' ' : '';
  while (anchor.firstChild) anchor.removeChild(anchor.firstChild);
  anchor.appendChild(doc.createTextNode(`${lead}${credit.text}${trail}`));
  if (credit.url) {
    anchor.setAttribute('href', credit.url);
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  } else {
    anchor.removeAttribute('href');
    anchor.removeAttribute('target');
    anchor.removeAttribute('rel');
  }
}

/** Replace an unlinked textual credit — `Designed by Foo` with no anchor. */
function rewriteTextCredit(root, credit, doc) {
  const walker = doc.createTreeWalker(root, 4 /* SHOW_TEXT */);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const value = node.nodeValue || '';
    const by = /\bby\b\s*:?\s*/i.exec(value);
    if (!by) continue;
    const head = value.slice(0, by.index);
    if (!CREDIT_KEYWORD.test(head.slice(-48))) continue;
    if (!node.parentElement || !inCreditScope(node.parentElement, root)) continue;
    // Everything from the phrase to the end of this text node is the credit.
    const phraseStart = phraseStartIndex(value, by.index);
    const anchor = doc.createElement('a');
    anchor.setAttribute('class', CREDIT_LINK_CLASS);
    applyCreditToAnchor(anchor, credit, doc);
    node.nodeValue = value.slice(0, phraseStart);
    node.parentNode.insertBefore(anchor, node.nextSibling);
    return true;
  }
  return false;
}

/** Walk back from a `by` to the start of the credit phrase that introduces it. */
function phraseStartIndex(value, byIndex) {
  const head = value.slice(0, byIndex);
  const match =
    /(?:designed?|developed?|hand[\s-]?coded|coded|built|made|crafted|created|website|web\s*site|web\s*design)[^<>]{0,45}$/i.exec(
      head
    );
  return match ? match.index : byIndex;
}

/** Append the credit where no credit exists yet. */
function insertCredit(root, credit, doc) {
  const anchor = doc.createElement('a');
  anchor.setAttribute('class', CREDIT_LINK_CLASS);
  applyCreditToAnchor(anchor, credit, doc);

  for (const selector of CREDIT_CONTAINERS) {
    const host = root.querySelector(selector);
    if (!host) continue;
    // Separate it from whatever the line already says (`© Copyright 2025 - Foo`),
    // but do not stack a dash on punctuation the line already ends with.
    const existing = (host.textContent || '').trimEnd();
    const separator = !existing ? '' : /[-–—·|.,;:]$/.test(existing) ? ' ' : ' — ';
    if (separator) host.appendChild(doc.createTextNode(separator));
    host.appendChild(anchor);
    return true;
  }
  for (const selector of FOOTER_TAILS) {
    const host = selector === ':scope' ? root : root.querySelector(selector);
    if (!host) continue;
    const line = doc.createElement('div');
    line.setAttribute('class', 'cs-bottom');
    const span = doc.createElement('span');
    span.setAttribute('class', 'cs-copyright');
    span.appendChild(anchor);
    line.appendChild(span);
    host.appendChild(line);
    return true;
  }
  return false;
}

/**
 * Swap the agency credit in a footer's HTML. Returns the HTML unchanged if
 * there is no DOMParser (the Worker-side scrape path) or nothing to do.
 */
export function applyAgencyCreditToHTML(html, credit = DEFAULT_AGENCY_CREDIT) {
  if (!html || typeof html !== 'string') return html || '';
  if (!credit || !credit.text) return html;
  if (typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;

  const anchor = findCreditAnchor(root);
  if (anchor) {
    applyCreditToAnchor(anchor, credit, doc);
    stripLeadingPhrase(anchor, root);
    return root.innerHTML;
  }
  if (rewriteTextCredit(root, credit, doc)) return root.innerHTML;
  if (insertCredit(root, credit, doc)) return root.innerHTML;
  return html;
}
